import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import MapSection from '../components/MapSection';
import { lawyersData } from '../data/lawyers';
import { citiesByState } from '../data/cities';

const DirectoryPage = () => {
  const location = useLocation();
  const [selectedService, setSelectedService] = useState('');
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedLawyers, setSelectedLawyers] = useState([]);

  const selectedCategory = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const categoria = params.get('categoria');
    return categoria || null;
  }, [location.search]);

  const serviceCategories = useMemo(() => [
    { key: 'servicios-legales', label: 'Servicios legales' },
    { key: 'salud', label: 'Salud' },
    { key: 'servicios-paralegales', label: 'Servicios paralegales' },
    { key: 'traducciones-certificadas', label: 'Traducciones Certificadas' },
    { key: 'aseo', label: 'Aseo' },
    { key: 'transporte', label: 'Transporte' },
    { key: 'belleza', label: 'Belleza' },
    { key: 'catering-y-eventos', label: 'Catering y eventos' },
    { key: 'tiendas-y-productos', label: 'Tiendas y productos' },
    { key: 'otros-servicios', label: 'Otros servicios' }
  ], []);

  const applyFiltersAndSort = useCallback((lawyers) => {
    let filtered = lawyers || [];
    
    if (selectedCategory) {
      filtered = filtered.filter((l) => Array.isArray(l.categoryKeys) && l.categoryKeys.includes(selectedCategory));
    }
    
    if (selectedService) {
      filtered = filtered.filter((l) => Array.isArray(l.categoryKeys) && l.categoryKeys.includes(selectedService));
    }

    return [...filtered].sort((a, b) => {
      if (a.backOfficeClient && !b.backOfficeClient) return -1;
      if (!a.backOfficeClient && b.backOfficeClient) return 1;
      if (a.isPremium && !b.isPremium) return -1;
      if (!a.isPremium && b.isPremium) return 1;
      return 0;
    });
  }, [selectedCategory, selectedService]);

  const states = useMemo(() => {
    if (!selectedService) {
      return Object.keys(lawyersData)
        .filter((s) => typeof s === 'string' && s.trim().length > 0)
        .sort((a, b) => a.localeCompare(b));
    }
    
    const statesWithService = new Set();
    Object.keys(lawyersData).forEach(state => {
      const lawyers = lawyersData[state] || [];
      const hasService = lawyers.some(l => 
        Array.isArray(l.categoryKeys) && l.categoryKeys.includes(selectedService)
      );
      if (hasService) {
        statesWithService.add(state);
      }
    });
    
    return Array.from(statesWithService).sort((a, b) => a.localeCompare(b));
  }, [selectedService]);

  const availableCities = useMemo(() => {
    if (!selectedState) return [];
    return citiesByState[selectedState] || [];
  }, [selectedState]);

  const applyCityFilter = useCallback((lawyers) => {
    if (!selectedCity) return lawyers || [];
    return (lawyers || []).filter((l) => l && l.city === selectedCity);
  }, [selectedCity]);

  const handleServiceSelect = (e) => {
    const value = e.target.value;
    setSelectedService(value);
    setSelectedState(null);
    setSelectedCity('');
    
    // Si se selecciona un servicio, mostrar todos los profesionales de ese servicio
    if (value) {
      const allLawyers = [];
      const seenFirms = new Set();
      
      Object.keys(lawyersData).forEach(state => {
        const lawyers = lawyersData[state] || [];
        lawyers.forEach(lawyer => {
          if (Array.isArray(lawyer.categoryKeys) && lawyer.categoryKeys.includes(value)) {
            // Crear identificador único basado en firmName o name + email
            const uniqueKey = `${lawyer.firmName || lawyer.name}-${lawyer.email}`;
            
            // Evitar duplicados
            if (!seenFirms.has(uniqueKey)) {
              seenFirms.add(uniqueKey);
              allLawyers.push(lawyer);
            }
          }
        });
      });
      
      setSelectedLawyers(allLawyers.sort((a, b) => {
        if (a.backOfficeClient && !b.backOfficeClient) return -1;
        if (!a.backOfficeClient && b.backOfficeClient) return 1;
        if (a.isPremium && !b.isPremium) return -1;
        if (!a.isPremium && b.isPremium) return 1;
        return 0;
      }));
    } else {
      setSelectedLawyers([]);
    }
  };

  const handleStateClick = (stateName) => {
    setSelectedState(stateName);
    setSelectedCity('');
    
    if (selectedService) {
      // Si hay un servicio seleccionado, filtrar por servicio también
      const lawyers = lawyersData[stateName] || [];
      const filtered = lawyers.filter(l => 
        Array.isArray(l.categoryKeys) && l.categoryKeys.includes(selectedService)
      );
      setSelectedLawyers(applyCityFilter(applyFiltersAndSort(filtered)));
    } else {
      const lawyers = lawyersData[stateName] || [];
      setSelectedLawyers(applyCityFilter(applyFiltersAndSort(lawyers)));
    }
  };

  const handleStateSelect = (e) => {
    const value = e.target.value;
    if (!value) {
      setSelectedState(null);
      setSelectedCity('');
      // Si hay servicio seleccionado, volver a mostrar todos los profesionales de ese servicio
      if (selectedService) {
        const allLawyers = [];
        const seenFirms = new Set();
        
        Object.keys(lawyersData).forEach(state => {
          const lawyers = lawyersData[state] || [];
          lawyers.forEach(lawyer => {
            if (Array.isArray(lawyer.categoryKeys) && lawyer.categoryKeys.includes(selectedService)) {
              // Crear identificador único basado en firmName o name + email
              const uniqueKey = `${lawyer.firmName || lawyer.name}-${lawyer.email}`;
              
              // Evitar duplicados
              if (!seenFirms.has(uniqueKey)) {
                seenFirms.add(uniqueKey);
                allLawyers.push(lawyer);
              }
            }
          });
        });
        
        setSelectedLawyers(allLawyers.sort((a, b) => {
          if (a.backOfficeClient && !b.backOfficeClient) return -1;
          if (!a.backOfficeClient && b.backOfficeClient) return 1;
          if (a.isPremium && !b.isPremium) return -1;
          if (!a.isPremium && b.isPremium) return 1;
          return 0;
        }));
      } else {
        setSelectedLawyers([]);
      }
      return;
    }

    setSelectedState(value);
    setSelectedCity('');
    
    if (selectedService) {
      // Si hay un servicio seleccionado, filtrar por servicio también
      const lawyers = lawyersData[value] || [];
      const filtered = lawyers.filter(l => 
        Array.isArray(l.categoryKeys) && l.categoryKeys.includes(selectedService)
      );
      setSelectedLawyers(applyCityFilter(applyFiltersAndSort(filtered)));
    } else {
      const lawyers = lawyersData[value] || [];
      setSelectedLawyers(applyCityFilter(applyFiltersAndSort(lawyers)));
    }
  };

  const handleCitySelect = (e) => {
    const value = e.target.value;
    setSelectedCity(value);

    if (!selectedState) return;
    const lawyers = lawyersData[selectedState] || [];
    const base = applyFiltersAndSort(lawyers);
    const filteredByCity = value ? base.filter((l) => l && l.city === value) : base;
    setSelectedLawyers(filteredByCity);
  };

  useEffect(() => {
    if (!selectedState) return;
    const lawyers = lawyersData[selectedState] || [];
    setSelectedLawyers(applyCityFilter(applyFiltersAndSort(lawyers)));
  }, [applyCityFilter, applyFiltersAndSort, selectedState]);

  return (
    <div className="directory-page">
      <MapSection 
        onStateClick={handleStateClick}
        selectedState={selectedState}
        selectedLawyers={selectedLawyers}
        showFilters
        showServiceFilter
        services={serviceCategories}
        selectedService={selectedService}
        states={states}
        selectedCity={selectedCity}
        availableCities={availableCities}
        onServiceSelect={handleServiceSelect}
        onStateSelect={handleStateSelect}
        onCitySelect={handleCitySelect}
      />
    </div>
  );
};

export default DirectoryPage;
